import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { formatCurrency } from './format';

// Tasa BCV (o custom_exchange_rate) - para dashboard y reportes
export function useDolarBCV() {
  return useQuery({
    queryKey: ['dolar-bcv'],
    queryFn: async () => {
      const { data } = await api.get('/api/dolar-bcv/');
      return data?.dolar_oficial ?? null;
    },
    refetchInterval: 60 * 60 * 1000,
  });
}

// Tasa para cálculo de precios de productos
// Prioridad: custom_exchange_rate > BCV[preferred_currency]
export function useProductPricingRate() {
  return useQuery({
    queryKey: ['product-pricing-rate'],
    queryFn: async () => {
      try {
        // Obtener configuración de la tienda
        const { data: storeData } = await api.get('/api/merchant/store');
        
        // Si hay tasa personalizada > 0, usarla
        if (storeData?.settings?.custom_exchange_rate && parseFloat(storeData.settings.custom_exchange_rate) > 0) {
          return {
            rate: parseFloat(storeData.settings.custom_exchange_rate),
            currency: storeData.settings.preferred_currency || 'USD',
            is_custom: true,
          };
        }
        
        // Si no, usar BCV según preferred_currency
        const { data: bcvData } = await api.get('/api/dolar-bcv/');
        const currency = storeData?.settings?.preferred_currency || 'USD';
        const rate = bcvData[currency] || bcvData.USD || 50;
        
        return {
          rate: parseFloat(rate),
          currency: currency,
          is_custom: false,
        };
      } catch (error) {
        console.error('Error fetching pricing rate:', error);
        return { rate: 50, currency: 'USD', is_custom: false };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// Hook para obtener la tasa desde una tienda pública (no requiere autenticación)
export function usePublicStorePricingRate(slug: string) {
  return useQuery({
    queryKey: ['public-pricing-rate', slug],
    queryFn: async () => {
      try {
        // Obtener configuración de la tienda pública
        const { data: storeData } = await api.get(`/api/stores/${slug}/public`);
        
        // Si hay tasa personalizada > 0, usarla
        if (storeData?.settings?.custom_exchange_rate && parseFloat(storeData.settings.custom_exchange_rate) > 0) {
          return {
            rate: parseFloat(storeData.settings.custom_exchange_rate),
            currency: storeData.settings.preferred_currency || 'USD',
            is_custom: true,
          };
        }
        
        // Si no, usar BCV según preferred_currency
        const { data: bcvData } = await api.get('/api/dolar-bcv/');
        const currency = storeData?.settings?.preferred_currency || 'USD';
        const rate = bcvData[currency] || bcvData.USD || 50;
        
        return {
          rate: parseFloat(rate),
          currency: currency,
          is_custom: false,
        };
      } catch (error) {
        console.error('Error fetching public pricing rate:', error);
        return { rate: 50, currency: 'USD', is_custom: false };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos - optimizado para costos AWS
    enabled: Boolean(slug),
  });
}

export function formatLocalCurrency(value: number, currency: string = 'VES') {
  let formatted = formatCurrency(value, currency);
  
  // Fix para VES: algunos navegadores muestran "s.S" o "S" en lugar de "Bs."
  if (currency === 'VES') {
    formatted = formatted.replace(/^s\.S\s/, 'Bs. ').replace(/^S\s/, 'Bs. ');
  }
  
  // Fix para COP: algunos navegadores muestran "Bs.S" en lugar de "$"
  if (currency === 'COP') {
    formatted = formatted.replace(/Bs\.S\s/, '$ ').replace(/^COL\$\s/, '$ ');
  }
  
  return formatted;
}

// Mantener formatCurrencyBs para compatibilidad (deprecado)
export function formatCurrencyBs(value: number) {
  return formatLocalCurrency(value, 'VES');
}

export function formatCurrencyUSD(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

// Convertir USD a Bs usando la tasa de productos
export function convertUSDtoBs(usdAmount: number, pricingRateData: any): number {
  if (!pricingRateData || !pricingRateData.rate) return 0;
  return Number(usdAmount) * Number(pricingRateData.rate);
}

// Formatear precio dual: USD (principal) y moneda local (calculado)
export function formatDualPrice(usdPrice: number, pricingRateData: any) {
  const usd = formatCurrencyUSD(usdPrice);
  const localCurrency = pricingRateData?.currency || 'VES';
  const localValue = convertUSDtoBs(usdPrice, pricingRateData);
  const local = pricingRateData?.rate ? formatLocalCurrency(localValue, localCurrency) : '--';
  
  return { 
    usd, 
    local,  // ← CAMBIO CLAVE: 'bs' → 'local'
    bs: local,  // ← Mantener para compatibilidad (deprecado)
    localValue,
    bsValue: localValue,  // ← Mantener para compatibilidad (deprecado)
    currency: localCurrency,
    is_custom: pricingRateData?.is_custom || false,
  };
}
